import React from 'react';
import moment from 'moment-timezone';
import './subscriptionSummaryListView.module.scss';
import { LoaderRow } from '../../../atoms/layout/LoaderRow';
import { MembershipItemId } from '../../../../../commonv3/types/membershipPlans';
import { SubscriptionSummary } from '../../../../types/subscriptionSummary';
import { PlanUpdateWarning } from './PlanUpdateWarning';
import { MembershipPlanGroupKey } from '../../../../types/membershipPlans';
import { usePlanOperationConfig } from '../../../../../commonv3/hooks/usePlanOperationConfig';
import { SubscriptionSummaryView } from './SubscriptionSummaryView';

function findNextSummary(summary: SubscriptionSummary, summaries: SubscriptionSummary[]): SubscriptionSummary {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return summaries.find((s) => s.parent_chain_id == summary.chain_id)!;
}

function isExpired(summary: SubscriptionSummary) {
  return !summary.suspended && moment(summary.expires_at).isSameOrBefore(moment().startOf('day'));
}

function getKey(summary: SubscriptionSummary) {
  return `${summary.plan_name}-${summary.starts_at}`;
}

const getChangingPlanSubscriptionSummaries = (summaries: SubscriptionSummary[]) =>
  summaries
    .filter((s) => !isExpired(s))
    .map((s) => findNextSummary(s, summaries))
    .filter((s) => !!s)
    .map(getKey);

const filterVisibleSubscriptionSummaries = (summaries: SubscriptionSummary[]) => {
  const changingPlanSummaries = getChangingPlanSubscriptionSummaries(summaries);
  return summaries.filter((s) => {
    const key = getKey(s);
    if (changingPlanSummaries.indexOf(key) >= 0) {
      return false;
    }
    return !isExpired(s);
  });
};

function isMulticreatorPlan(summary: SubscriptionSummary) {
  // @ts-expect-error ts2339
  return summary.plan_group_key == MembershipPlanGroupKey.MULTICREATOR;
}

const oneTimePurchaseSummaries = (summaries: SubscriptionSummary[]) => {
  const multicreatorSummary = summaries.filter((s) => {
    return isMulticreatorPlan(s);
  });
  const otherSummaries = summaries.filter((s) => {
    return !s.subscribed && !isMulticreatorPlan(s);
  });
  return [...multicreatorSummary, ...otherSummaries];
};
const subscriptionSummaries = (summaries: SubscriptionSummary[]) => {
  return summaries.filter((s) => {
    return s.subscribed;
  });
};

type Props = {
  summaries: SubscriptionSummary[];
  loading?: boolean;
  isWithdrawalSet: boolean;
  onUpdateRequest?: (itemId: MembershipItemId) => void;
  onUpdateCancelRequest?: (originalItemId: MembershipItemId, nextItemId: MembershipItemId) => void;
  onCancelSuspensionRequest?: (subscriptionCode: string, subscriptionSuspensionCode: string) => Promise<void>;
  onResumeSubscriptionRequest?: (subscriptionCode: string) => void;
};

export const SubscriptionSummaryListView = ({
  summaries,
  loading = false,
  isWithdrawalSet,
  onUpdateRequest,
  onUpdateCancelRequest,
  onCancelSuspensionRequest,
  onResumeSubscriptionRequest,
}: Props) => {
  const config = usePlanOperationConfig();
  if (loading) {
    return <LoaderRow />;
  }
  const subscribedSummaries = subscriptionSummaries(filterVisibleSubscriptionSummaries(summaries));

  return (
    <div id="subscription-summary-list" styleName="subscription-summary-list">
      {oneTimePurchaseSummaries(filterVisibleSubscriptionSummaries(summaries)).map((summary) => {
        return (
          <SubscriptionSummaryView
            key={`${summary.plan_name}${summary.starts_at}`}
            summary={summary}
            isWithdrawalSet={isWithdrawalSet}
            onUpdateRequest={onUpdateRequest}
            onUpdateCancelRequest={onUpdateCancelRequest}
            onCancelSuspensionRequest={onCancelSuspensionRequest}
            onResumeSubscriptionRequest={onResumeSubscriptionRequest}
          />
        );
      })}
      {!isWithdrawalSet && subscribedSummaries.length > 0 && config.planUpdateEnabled && <PlanUpdateWarning />}
      {subscribedSummaries.map((summary) => {
        return (
          <SubscriptionSummaryView
            key={`${summary.plan_name}${summary.starts_at}`}
            summary={summary}
            isWithdrawalSet={isWithdrawalSet}
            onUpdateRequest={onUpdateRequest}
            onUpdateCancelRequest={onUpdateCancelRequest}
            onCancelSuspensionRequest={onCancelSuspensionRequest}
            onResumeSubscriptionRequest={onResumeSubscriptionRequest}
          />
        );
      })}
    </div>
  );
};
